import { BehaviorSubject, Observable } from 'rxjs';
import { IChatMessage } from '../interfaces/chat-message.interface';

export class ChatMessagesStore {
  private messages$ = new BehaviorSubject<IChatMessage[]>([]);

  getMessages$(): Observable<IChatMessage[]> {
    return this.messages$.asObservable();
  }

  getMessages(): IChatMessage[] {
    return this.messages$.getValue();
  }

  getLastMessage(): IChatMessage | undefined {
    const messages = this.messages$.getValue();
    return messages[messages.length - 1];
  }

  /**
   * Casa por `id` persistido ou por `localId` otimista: uma resposta em curso é
   * criada com localId e só recebe o id do servidor no `run.started`.
   */
  findIndexById(messageId: string): number {
    return this.messages$
      .getValue()
      .findIndex((m) => m.id === messageId || m.localId === messageId);
  }

  patchById(
    messageId: string,
    update: (message: IChatMessage) => Partial<IChatMessage>
  ): boolean {
    const messages = this.messages$.getValue();
    const index = this.findIndexById(messageId);
    if (index === -1) {
      return false;
    }

    const updated = [...messages];
    updated[index] = { ...messages[index], ...update(messages[index]) };
    this.messages$.next(updated);
    return true;
  }

  assignServerId(localId: string, serverId: string): void {
    this.patchById(localId, () => ({ id: serverId }));
  }

  set(messages: IChatMessage[]): void {
    this.messages$.next(messages);
  }

  add(message: IChatMessage): void {
    this.messages$.next([...this.messages$.getValue(), message]);
  }

  addMany(messages: IChatMessage[]): void {
    if (!messages.length) {
      return;
    }
    this.messages$.next([...this.messages$.getValue(), ...messages]);
  }

  remove(localIds: string[]): void {
    const ids = new Set(localIds);
    this.messages$.next(
      this.messages$.getValue().filter((m) => !ids.has(m.localId))
    );
  }

  updateByLocalId(
    localId: string,
    update: (message: IChatMessage) => Partial<IChatMessage>
  ): void {
    const messages = this.messages$.getValue();
    const index = messages.findIndex((m) => m.localId === localId);
    if (index === -1) {
      return;
    }

    const updated = [...messages];
    updated[index] = { ...messages[index], ...update(messages[index]) };
    this.messages$.next(updated);
  }

  clear(): void {
    this.messages$.next([]);
  }
}
